import useSharingRulePresetInfo from '@/composables/dataSharingHub/useSharingRulePresetInfo';
import {
  useDataSharesStore,
  usePopulationsStore,
  useSourcesStore,
} from '@/stores';
import { sortObjectsByMdmType } from '@/utils';

import type { SharingSideType } from '../types';

type SharedFieldType = {
  id: number;
  display_name: string;
  table: string;
  mdm_type: string | null;
};

type SharedFieldsSourceType = {
  tableName: string;
  mdm_type: string | null;
  fields: { id: number; display_name: string }[];
};

export type SharedFieldsType = {
  /** Unset on the partner side: incoming rules do not say which preset the partner uses. */
  presetName?: string;
  sources: SharedFieldsSourceType[];
};

function groupByTable(fields: SharedFieldType[]): SharedFieldsSourceType[] {
  const sourcesByTable = new Map<string, SharedFieldsSourceType>();
  fields.forEach(({ id, display_name, table, mdm_type }) => {
    const source = sourcesByTable.get(table) ?? {
      tableName: table,
      mdm_type,
      fields: [],
    };
    source.fields.push({ id, display_name });
    sourcesByTable.set(table, source);
  });
  return sortObjectsByMdmType([...sourcesByTable.values()]);
}

/**
 * Fields shared for one population of a By Partners row: ours with the
 * partner on the outgoing side, the partner's with us on the incoming side.
 */
export default function usePartnerTabSharedFields() {
  const dataSharesStore = useDataSharesStore();
  const populationsStore = usePopulationsStore();
  const sourcesStore = useSourcesStore();
  const { getPresetInfoForRule } = useSharingRulePresetInfo();

  function toSharedField(sourceFieldId: number): SharedFieldType[] {
    const field = sourcesStore.getSourceFieldById(sourceFieldId);
    if (!field?.id || !field.source_id) return [];
    const source = sourcesStore.getSourceById(field.source_id);
    if (!source) return [];
    return [
      {
        id: field.id,
        display_name: field.display_name,
        table: source.table,
        mdm_type: source.mdm_type,
      },
    ];
  }

  function getOutgoingSharedFields(
    populationId: number,
    partnerOrgId: number,
  ): SharedFieldsType | null {
    const rule = dataSharesStore.outgoingRuleLookup(populationId, partnerOrgId);
    const population = populationsStore.getPopulationById(populationId);
    if (!rule || !population) return null;

    /* Muncher fills a rule's fields from its preset, so a rule without one
       shares none. Its preset title would read as the Crossbeam preset, so
       the empty state goes without one. */
    const sources = groupByTable(rule.source_field_ids.flatMap(toSharedField));
    return {
      presetName: sources.length
        ? getPresetInfoForRule(rule, population)?.presetName
        : undefined,
      sources,
    };
  }

  function getIncomingSharedFields(
    partnerPopulationId: number,
  ): SharedFieldsType {
    return {
      sources: groupByTable(
        dataSharesStore.incomingSharedFieldsLookup(partnerPopulationId),
      ),
    };
  }

  function getSharedFields(
    side: SharingSideType,
    populationId: number,
    partnerOrgId: number,
  ): SharedFieldsType | null {
    return side === 'outgoing'
      ? getOutgoingSharedFields(populationId, partnerOrgId)
      : getIncomingSharedFields(populationId);
  }

  return { getSharedFields };
}
